import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { SparkleCircle } from '@lifeomic/chromicons';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import moment from 'moment';
import { memberDetailMessages } from './messages';
import { DocumentExtractionV2Modal } from './DocumentExtractionV2Modal';

const questionnaireResponseFhirService = new FhirService(
  'QuestionnaireResponse',
);

const getSurveyName = (resource: fhir.QuestionnaireResponse): string => {
  const questionnaire = (
    resource as {
      questionnaire?: string | { display?: string; reference?: string };
    }
  ).questionnaire;
  if (typeof questionnaire === 'string') {
    return questionnaire || resource.id || '—';
  }
  return (
    questionnaire?.display || questionnaire?.reference || resource.id || '—'
  );
};

const getAuthoredTimeMs = (resource: fhir.QuestionnaireResponse): number =>
  resource.authored ? new Date(resource.authored).getTime() : 0;

const formatAuthoredDate = (resource: fhir.QuestionnaireResponse): string =>
  resource.authored ? moment(resource.authored).format('M/D/YYYY') : '—';

type SurveyResponseRow = {
  id: string;
  resource: fhir.QuestionnaireResponse;
  survey: string;
  date: string;
};

const useStyles = makeStyles(theme => ({
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
  },
}));

export type SurveyResponsesExtractionProps = {
  memberId: string;
  fallback: React.ReactNode;
};

export const SurveyResponsesExtraction: React.FC<
  SurveyResponsesExtractionProps
> = ({ memberId, fallback }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const [fetchResponses, fetchState] = useService(
    questionnaireResponseFhirService.get,
  );
  const [responses, setResponses] = useState<fhir.QuestionnaireResponse[]>([]);
  const [extractionTarget, setExtractionTarget] =
    useState<fhir.QuestionnaireResponse | null>(null);

  const loadResponses = useCallback(async () => {
    if (!accountId || !memberId || !auth) return;
    try {
      const response = await fetchResponses(accountId, {
        subject: `Patient/${memberId}`,
        status: 'completed',
        _count: 200,
      });
      const entry = response?.data?.entry ?? [];
      const resources = entry
        .map((e: { resource?: fhir.QuestionnaireResponse }) => e.resource)
        .filter(Boolean) as fhir.QuestionnaireResponse[];
      setResponses(resources);
    } catch {
      setResponses([]);
    }
  }, [accountId, memberId, auth, fetchResponses]);

  useEffect(() => {
    void loadResponses();
  }, [loadResponses]);

  const sortedResponses = useMemo(
    () =>
      [...responses].sort(
        (a, b) => getAuthoredTimeMs(b) - getAuthoredTimeMs(a),
      ),
    [responses],
  );

  const rows: SurveyResponseRow[] = sortedResponses.map(resource => ({
    id: resource.id ?? '',
    resource,
    survey: getSurveyName(resource),
    date: formatAuthoredDate(resource),
  }));

  const columns = [
    {
      header: {
        label: formatMessage(memberDetailMessages.surveysSurveyColumn),
      },
      cell: { content: (row: SurveyResponseRow) => row.survey },
    },
    {
      header: { label: formatMessage(memberDetailMessages.surveysDateColumn) },
      cell: { content: (row: SurveyResponseRow) => row.date },
    },
  ];

  const rowActions = useMemo(
    () => (row: SurveyResponseRow) =>
      (
        <Tooltip
          title={formatMessage(
            memberDetailMessages.memberSurveyExtractionButton,
          )}
        >
          <IconButton
            icon={SparkleCircle}
            size={0}
            color="inverse"
            aria-label={formatMessage(
              memberDetailMessages.memberSurveyExtractionButton,
            )}
            onClick={event => {
              event.stopPropagation();
              event.currentTarget.blur();
              setExtractionTarget(row.resource);
            }}
          />
        </Tooltip>
      ),
    [formatMessage],
  );

  if (fetchState.isFetching && responses.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <div className={classes.tabPanel}>
      <DocumentExtractionV2Modal
        isOpen={!!extractionTarget}
        questionnaireResponse={extractionTarget}
        subjectId={memberId}
        onDismiss={() => setExtractionTarget(null)}
      />
      <TableModule
        isLoading={fetchState.isFetching}
        config={columns}
        data={rows}
        noResultsMessage={formatMessage(
          memberDetailMessages.surveysResponsesEmpty,
        )}
        rowActions={rowActions}
      />
    </div>
  );
};
